import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Edit2, Phone, Calendar, BookOpen, Sliders, Users, ShieldCheck, ChevronRight, UserPlus, Copy, Share2, Link2, RefreshCw, X, LogOut, Check, Pencil } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AppShell from "../components/app/AppShell";
import { COMPANY_PHONE, COMPANY_PHONE_TEL, CALENDLY_URL } from "../lib/brand";
import { BACKEND_URL } from "../lib/api";

function ActionRow({ icon: Icon, color, title, sub, onClick, testId }) {
  return (
    <button data-testid={testId} onClick={onClick} className="w-full flex items-center gap-4 p-5 mt-3 bg-white rounded-2xl border hover:bg-[#FAFAFA] transition" style={{ borderColor: "#E8E8E8" }}>
      <Icon size={18} color={color} />
      <div className="flex-1 text-left">
        <div className="font-bold text-[15px]">{title}</div>
        <div className="text-xs mt-0.5" style={{ color: "#5A5A5A" }}>{sub}</div>
      </div>
      <ChevronRight size={18} color="#8A8A8A" />
    </button>
  );
}

export default function Profile() {
  const { user, signOut, refresh } = useAuth();
  const navigate = useNavigate();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteCode, setInviteCode] = useState(user?.invite_code || null);
  const [editingCode, setEditingCode] = useState(false);
  const [codeDraft, setCodeDraft] = useState("");
  const [savingCode, setSavingCode] = useState(false);
  const [codeErr, setCodeErr] = useState("");
  const isManager = user?.role === "admin" || user?.role === "agent";
  const isAdmin = user?.role === "admin";
  const isAgent = user?.role === "agent";

  useEffect(() => {
    if (isManager && !inviteCode) {
      api("/auth/invite-code").then((r) => setInviteCode(r.invite_code)).catch(() => {});
    }
  }, [isManager, inviteCode]);

  if (!user) return null;
  const inviteLink = inviteCode ? `${BACKEND_URL || ""}/register?code=${inviteCode}` : "";

  const onSignOut = () => { signOut(); navigate("/", { replace: true }); };
  const copy = (text, msg) => { navigator.clipboard.writeText(text); alert(msg); };
  const copyCode = () => copy(inviteCode, "Invite code copied!");
  const copyLink = () => copy(inviteLink, "Invite link copied!");
  const shareInvite = async () => {
    if (!inviteCode) return;
    const message = `Join ${user.agency || "our agency"} as a referral partner. Register here: ${inviteLink}\n\nOr enter code at signup: ${inviteCode}`;
    try { if (navigator.share) await navigator.share({ title: "Partner Invite", text: message }); else await copyLink(); } catch {}
  };
  const regenerateCode = async () => {
    if (!window.confirm("Anyone using your old code won't be able to register anymore. Continue?")) return;
    try { const r = await api("/auth/invite-code/regenerate", { method: "POST" }); setInviteCode(r.invite_code); refresh && (await refresh()); }
    catch (e) { alert(e.message); }
  };
  const startEditCode = () => { setCodeDraft(inviteCode || ""); setCodeErr(""); setEditingCode(true); };
  const saveCustomCode = async () => {
    const raw = codeDraft.trim().toUpperCase();
    if (raw === (inviteCode || "")) { setEditingCode(false); return; }
    if (!/^[A-Z0-9-]{4,16}$/.test(raw)) { setCodeErr("4-16 chars: letters, numbers, hyphens only."); return; }
    setSavingCode(true); setCodeErr("");
    try {
      const r = await api("/auth/invite-code/set", { method: "POST", body: { code: raw } });
      setInviteCode(r.invite_code);
      setEditingCode(false);
      refresh && (await refresh());
    } catch (e) {
      setCodeErr(e.message || "Could not save");
    } finally { setSavingCode(false); }
  };

  const roleColor = isAdmin ? "#D62828" : isAgent ? "#1F4E79" : "#2E78C5";
  const roleLabel = isAdmin ? "ADMIN" : isAgent ? "AGENT" : "PARTNER";

  return (
    <AppShell>
      <div className="px-5 lg:px-10 py-8 max-w-3xl">
        <div className="bg-white rounded-3xl border p-7 text-center" style={{ borderColor: "#E8E8E8" }}>
          <div className="w-20 h-20 rounded-full grid place-items-center mx-auto" style={{ background: "#E3F0FB" }}>
            <span className="font-display text-3xl font-black" style={{ color: "#D62828" }}>{(user.name || "U").charAt(0).toUpperCase()}</span>
          </div>
          <h1 className="font-display text-2xl font-black mt-4">{user.name}</h1>
          <p className="text-sm" style={{ color: "#5A5A5A" }}>{user.email}</p>
          <div className="inline-flex items-center gap-1.5 mt-3 px-3 py-1 rounded-full text-white font-extrabold text-[10px] tracking-wider" style={{ background: roleColor }}>
            <ShieldCheck size={11} /> {roleLabel}
          </div>
        </div>

        {isManager && inviteCode && (
          <div className="rounded-2xl p-5 mt-5 border" style={{ background: "#E3F0FB", borderColor: "#2E78C5" }}>
            <div className="flex items-center gap-2">
              <UserPlus size={16} color="#D62828" />
              <h3 className="font-extrabold" style={{ color: "#D62828" }}>Your Invite Code</h3>
            </div>
            <p className="text-xs mt-1.5" style={{ color: "#5A5A5A" }}>Share this with new {isAdmin ? "partners" : "referral partners"}. They'll auto-link to your team when they register.</p>

            {editingCode ? (
              <div className="mt-3">
                <div className="flex items-center gap-2">
                  <input
                    data-testid="edit-invite-input"
                    autoFocus
                    value={codeDraft}
                    onChange={(e) => setCodeDraft(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ""))}
                    maxLength={16}
                    placeholder="MY-CODE"
                    className="flex-1 rounded-full px-4 py-2.5 font-display font-black text-lg tracking-widest text-white outline-none"
                    style={{ background: "#D62828", letterSpacing: "0.15em" }}
                  />
                  <button data-testid="save-invite-btn" onClick={saveCustomCode} disabled={savingCode} className="w-10 h-10 rounded-full grid place-items-center text-white" style={{ background: "#2E7D32" }}>
                    {savingCode ? <RefreshCw className="animate-spin" size={16} /> : <Check size={16} />}
                  </button>
                  <button data-testid="cancel-invite-btn" onClick={() => { setEditingCode(false); setCodeErr(""); }} className="w-10 h-10 rounded-full grid place-items-center bg-white border" style={{ borderColor: "#E8E8E8" }}>
                    <X size={16} />
                  </button>
                </div>
                {codeErr && <div className="text-xs font-bold mt-2" style={{ color: "#C62828" }}>{codeErr}</div>}
                <p className="text-[11px] italic mt-2" style={{ color: "#5A5A5A" }}>4–16 characters. Letters, numbers, and hyphens only. Case is auto-uppercased.</p>
              </div>
            ) : (
              <button onClick={copyCode} data-testid="copy-invite-code" className="inline-flex items-center gap-2 mt-3 px-4 py-2.5 rounded-full text-white font-display font-black text-lg tracking-widest" style={{ background: "#D62828" }}>
                {inviteCode} <Copy size={14} />
              </button>
            )}

            {!editingCode && (
              <div className="flex gap-2 mt-3 flex-wrap">
                <button onClick={shareInvite} data-testid="share-invite-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border text-xs font-extrabold" style={{ borderColor: "#E8E8E8", color: "#D62828" }}><Share2 size={14} /> Share Link</button>
                <button onClick={() => setShowInviteModal(true)} data-testid="show-invite-link-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border text-xs font-extrabold" style={{ borderColor: "#E8E8E8", color: "#D62828" }}><Link2 size={14} /> Show Link</button>
                <button onClick={startEditCode} data-testid="edit-invite-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border text-xs font-extrabold" style={{ borderColor: "#E8E8E8", color: "#D62828" }}><Pencil size={14} /> Edit Code</button>
                <button onClick={regenerateCode} data-testid="regen-invite-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-extrabold" style={{ background: "#fef3c7", color: "#92400e" }}><RefreshCw size={14} /> Random</button>
              </div>
            )}
          </div>
        )}

        <div className="bg-white rounded-2xl border p-5 mt-5" style={{ borderColor: "#E8E8E8" }}>
          <div className="flex justify-between py-2 border-b" style={{ borderColor: "#E8E8E8" }}><span className="text-xs uppercase tracking-wider font-extrabold" style={{ color: "#5A5A5A" }}>Agency</span><span className="font-bold">{user.agency || "—"}</span></div>
          <div className="flex justify-between py-2 border-b" style={{ borderColor: "#E8E8E8" }}><span className="text-xs uppercase tracking-wider font-extrabold" style={{ color: "#5A5A5A" }}>Phone</span><span className="font-bold">{user.phone || "—"}</span></div>
          <div className="flex justify-between py-2"><span className="text-xs uppercase tracking-wider font-extrabold" style={{ color: "#5A5A5A" }}>Member Since</span><span className="font-bold">{new Date(user.created_at).toLocaleDateString()}</span></div>
        </div>

        <ActionRow icon={Edit2} color="#D62828" title="Edit Profile" sub="Name, email, phone, password" onClick={() => navigate("/edit-profile")} testId="edit-profile-btn" />
        <a data-testid="contact-call-btn" href={`tel:${COMPANY_PHONE_TEL}`} className="w-full flex items-center gap-4 p-5 mt-3 bg-white rounded-2xl border" style={{ borderColor: "#E8E8E8" }}>
          <Phone size={18} color="#D62828" />
          <div className="flex-1"><div className="font-bold text-[15px]">Call Case Team</div><div className="text-xs mt-0.5" style={{ color: "#5A5A5A" }}>{COMPANY_PHONE}</div></div>
          <ChevronRight size={18} color="#8A8A8A" />
        </a>
        <a data-testid="contact-calendly-btn" href={CALENDLY_URL} target="_blank" rel="noreferrer" className="w-full flex items-center gap-4 p-5 mt-3 bg-white rounded-2xl border" style={{ borderColor: "#E8E8E8" }}>
          <Calendar size={18} color="#2E78C5" />
          <div className="flex-1"><div className="font-bold text-[15px]">Schedule a Partner Call</div><div className="text-xs mt-0.5" style={{ color: "#5A5A5A" }}>30-min Calendly</div></div>
          <ChevronRight size={18} color="#8A8A8A" />
        </a>
        <ActionRow icon={BookOpen} color="#2E78C5" title="Lead Quality Guide" sub="What to send, by tier" onClick={() => navigate("/lead-guide")} testId="lead-guide-btn" />
        {isManager && <ActionRow icon={Sliders} color="#D62828" title={isAdmin ? "Compensation Settings" : "My Compensation Grid"} sub={isAdmin ? "Edit tier rates" : "Set what you pay your partners"} onClick={() => navigate("/settings")} testId="settings-btn" />}
        {isAdmin && <ActionRow icon={ShieldCheck} color="#D62828" title="Manage Agents" sub="Add downline agents in your agency" onClick={() => navigate("/manage-agents")} testId="manage-agents-btn" />}
        {isManager && <ActionRow icon={Users} color="#D62828" title={isAgent ? "Manage My Team" : "Manage Partners"} sub={isAgent ? "Your referral partners" : "Archive or delete partner accounts"} onClick={() => navigate("/manage-partners")} testId="manage-partners-btn" />}

        <button data-testid="signout-btn" onClick={onSignOut} className="w-full flex items-center gap-4 p-5 mt-5 bg-white rounded-2xl border-2 transition" style={{ borderColor: "#FFEBEE" }}>
          <LogOut size={18} color="#C62828" />
          <span className="font-bold text-[15px]" style={{ color: "#C62828" }}>Sign Out</span>
        </button>
      </div>

      {showInviteModal && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-5" onClick={() => setShowInviteModal(false)}>
          <div className="w-full max-w-md bg-white rounded-3xl p-7" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between"><h3 className="font-display text-xl font-black">Your Invite Link</h3><button onClick={() => setShowInviteModal(false)}><X size={22} /></button></div>
            <p className="text-sm mt-4 p-3 rounded-xl break-all font-mono" style={{ background: "#F5F5F5" }}>{inviteLink}</p>
            <div className="flex gap-2 mt-4">
              <button onClick={copyLink} className="btn-primary flex-1"><Copy size={14} /> Copy Link</button>
              <button onClick={() => setShowInviteModal(false)} className="btn-ghost flex-1">Close</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
